import { ComponentBase } from "../core/ComponentBase.js";

function debounce(fn, wait) {
    let t;
    return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

export class AppMenu extends ComponentBase {
    static styles = `
    /* Base AppMenu container styles */
    .app-menu-container {
        display: flex;
        background-color: var(--bs-body-bg);
        color: var(--bs-body-color);
        transition: width 0.3s ease-in-out, height 0.3s ease-in-out;
    }

    /* Horizontal (Mobile Default) Specific Styles */
    .app-menu-container.horizontal {
        flex-direction: row;
        justify-content: space-around;
        align-items: center;
        border-top: 1px solid var(--bs-border-color);
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: var(--ab-z-header);
        padding: 0.2rem 0;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    .app-menu-container.horizontal::-webkit-scrollbar {
        display: none;
    }
    .app-menu-container.horizontal .app-menu-item {
        flex-direction: column;
        min-width: 60px;
        padding: 0.25rem 0.5rem;
        flex-shrink: 0; 
    }
    .app-menu-container.horizontal .app-menu-icon {
        font-size: var(--ab-text-lg);
        margin-bottom: 0.1rem;
    }
    .app-menu-container.horizontal .app-menu-label {
        font-size: var(--ab-text-2xs);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
    /* Hide submenus in horizontal mode */
    .app-menu-container.horizontal .app-submenu {
        display: none !important;
    }

    /* Vertical (Desktop Default / Mobile Sidebar Override) Specific Styles */
    .app-menu-container.vertical {
        flex-direction: column;
        width: 250px; /* Default expanded width */
        position: relative;
        border-right: 1px solid var(--bs-border-color);
    }
    .app-menu-container.vertical.app-menu-full-height {
        height: 100%;
    }
    .app-menu-container.vertical.app-menu-auto-height {
        height: auto;
    }
    .app-menu-container.vertical .menu-items-scroll-area {
        flex-grow: 1;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;
        scrollbar-color: var(--bs-border-color-translucent) transparent;
    }
    .app-menu-container.vertical .menu-items-scroll-area::-webkit-scrollbar { width: 6px; }
    .app-menu-container.vertical .menu-items-scroll-area::-webkit-scrollbar-track { background: transparent; }
    .app-menu-container.vertical .menu-items-scroll-area::-webkit-scrollbar-thumb { background-color: var(--bs-border-color-translucent); border-radius: var(--ab-radius-pill); }

    .app-menu-container.vertical .app-menu-item {
        flex-direction: row;
        justify-content: flex-start;
        padding: 0.75rem 1rem;
        width: 100%;
        text-align: left;
        position: relative;
    }
    .app-menu-container.vertical .app-menu-icon {
        font-size: var(--ab-text-lg);
        margin-right: 0.75rem;
    }
    .app-menu-container.vertical .app-menu-label {
        font-size: var(--ab-text-sm);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        opacity: 1;
        transition: opacity 0.1s 0.1s ease-in-out;
    }

    /* Submenu styles */
    .app-menu-item.has-submenu .submenu-toggle {
        margin-left: auto;
        padding: 0.25rem;
        background: none;
        border: none;
        color: var(--bs-body-color);
        font-size: var(--ab-text-xs);
        cursor: pointer;
        transition: transform var(--ab-t-fast);
    }
    .app-menu-item.has-submenu.expanded .submenu-toggle {
        transform: rotate(90deg);
    }
    .app-submenu {
        margin-left: 1rem;
        overflow: hidden;
    }
    /* Submenu header style using .ab-section-label tokens */
    .app-submenu-header {
        padding: 0.6rem 1rem 0.3rem 1rem;
        font-size: var(--ab-label-size);
        font-weight: var(--ab-label-weight);
        color: var(--ab-label-color);
        text-transform: uppercase;
        letter-spacing: var(--ab-label-spacing);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        user-select: none;
    }

    .app-submenu-header.active-parent {
        color: var(--bs-primary);
    }

    .app-submenu.collapsed {
        max-height: 0;
    }
    .app-submenu.expanded {
        max-height: 500px; /* Adjust based on expected content */
    }
    .app-submenu .app-menu-item {
        padding: 0.5rem 1rem;
        font-size: var(--ab-text-xs);
        border-left: none;
    }
    .app-submenu .app-menu-item .app-menu-icon {
        font-size: var(--ab-text-md);
        margin-right: 0.5rem;
    }

    /* Styles for Collapsed Vertical Menu (Icons Only) - Desktop */
    .app-menu-container.vertical.icons-only {
        width: 60px; /* Collapsed width */
    }
    .app-menu-container.vertical.icons-only .app-menu-label {
        display: none; /* Hide label */
        opacity: 0;
    }
    .app-menu-container.vertical.icons-only .app-menu-item {
        justify-content: center; /* Center icon */
        padding: 0.75rem 0.5rem;
    }
    .app-menu-container.vertical.icons-only .app-menu-icon {
        margin-right: 0;
        font-size: var(--ab-text-lg);
    }
    .app-menu-container.vertical.icons-only .app-menu-toggle-button-container {
        justify-content: center;
    }
    /* Hide submenus and submenu toggles when collapsed */
    .app-menu-container.vertical.icons-only .submenu-toggle,
    .app-menu-container.vertical.icons-only .app-submenu {
        display: none !important;
    }

    /* General Menu Item Styles */
    .app-menu-item {
        display: flex;
        align-items: center;
        text-decoration: none;
        color: var(--bs-body-color);
        transition: background-color var(--ab-t-base), color var(--ab-t-base), padding 0.3s ease-in-out;
        overflow: hidden;
        border-radius: var(--ab-radius-sm);
        cursor: pointer;
    }
    .app-menu-item.active {
        color: var(--ab-active-color);
        background-color: var(--ab-active-bg);
        font-weight: var(--ab-weight-medium);
    }
    @media (hover: hover) {
        .app-menu-item:hover {
            background-color: var(--ab-hover-bg);
        }
    }
    .app-menu-item:focus-visible {
        outline: none;
        box-shadow: var(--ab-focus-ring);
    }
    /* Parent items with active children styling */
    .app-menu-item.has-active-child {
        color: var(--bs-primary);
        font-weight: var(--ab-weight-medium);
    }

    /* Toggle Button Styles - Only for Vertical Collapsible Menu */
    .app-menu-toggle-button-container {
        flex-shrink: 0;
        padding: 0.5rem;
        display: flex;
        border-top: 1px solid var(--bs-border-color);
        margin-top: auto; /* Pushes to the bottom */
    }
    .app-menu-container.vertical:not(.icons-only) .app-menu-toggle-button-container {
        justify-content: flex-end;
    }
    .app-menu-toggle-button {
        background: none;
        border: none;
        color: var(--bs-secondary-color);
        font-size: var(--ab-text-lg);
        padding: 0.25rem 0.5rem;
        cursor: pointer;
        border-radius: var(--ab-radius-sm);
        transition: color var(--ab-t-fast), background-color var(--ab-t-fast);
    }
    @media (hover: hover) {
        .app-menu-toggle-button:hover {
            color: var(--bs-primary);
            background-color: var(--ab-hover-bg);
        }
    }
    .app-menu-toggle-button:focus-visible {
        outline: none;
        box-shadow: var(--ab-focus-ring);
    }
    .app-menu-toggle-button:active {
        background-color: var(--ab-hover-bg);
        opacity: 0.8;
    }
    /* Hide toggle button if menu is not vertical or not collapsible */
    .app-menu-container:not(.vertical) .app-menu-toggle-button-container,
    .app-menu-container.vertical:not(.collapsible) .app-menu-toggle-button-container {
        display: none;
    }
`;


    constructor(config) {
        super(config);
        this.menuData = [];
        this.container = null;
        this.itemsContainer = null;
        this.toggleButton = null;
        this.routeMap = {};
        this.routesSorted = [];
        this.tooltipInstances = [];
        this.expandedSubmenus = new Set(); // Track expanded submenus

        this.isVertical = false;
        this.isCollapsed = this.config.defaultCollapsed;
        this._needsFullRender = true;

        const instanceKey = this.config.instanceId || this.instanceId || this.eventGroup;
        this.containerId = `app-menu-container-${instanceKey}`;
        this.localStorageKey = `appMenu-${instanceKey}-collapsedState`;
        this.submenuStorageKey = `appMenu-${instanceKey}-expandedSubmenus`;

        // Bind handlers
        this.handleResize = this.handleResize.bind(this);
        this.debouncedHandleResize = debounce(this.handleResize, 100).bind(this);
        this.handleRouteChange = this.handleRouteChange.bind(this);
        this.handleLoginLogout = this.handleLoginLogout.bind(this);
        this.toggleCollapse = this.toggleCollapse.bind(this);
        this.modalShowHandler = () => this.handleModalVisibilityChange(true);
        this.modalHideHandler = () => this.handleModalVisibilityChange(false);

        // Load saved states
        if (this.config.saveState && this.config.collapsible) {
            const stored = localStorage.getItem(this.localStorageKey);
            if (stored !== null) this.isCollapsed = JSON.parse(stored);
        }

        if (this.config.saveState) {
            const storedSubmenus = localStorage.getItem(this.submenuStorageKey);
            if (storedSubmenus) {
                try {
                    this.expandedSubmenus = new Set(JSON.parse(storedSubmenus));
                } catch (e) {
                    this.expandedSubmenus = new Set();
                }
            }
        }

        this.applyStyles();
    }

    static getDefaultConfig() {
        return {
            dataSource: "layoutEngine",
            startingRoute: "/user",
            includeHidden: false,
            menuConfig: null,
            layout: 'auto',
            mobileBehavior: 'bottom',
            fullHeight: true,
            collapsible: false,
            defaultCollapsed: false,
            saveState: false,
            showSubmenus: false, // New option to enable/disable submenu support
        };
    }

    async initialize() {
        this.updateLayoutType();
        if (this.config.dataSource === "layoutEngine") {
            // Use hierarchical menu structure instead of flat
            this.menuData = this.config.showSubmenus
                ? this.appClient.layoutEngine.getMenuStructure(
                    this.config.startingRoute,
                    this.config.includeHidden
                )
                : this.appClient.layoutEngine.getFlatMenuStructure(
                    this.config.startingRoute,
                    this.config.includeHidden
                );
        } else {
            this.menuData = this.config.menuConfig || [];
        }

        this.adjustMenuRelatedViewportHeight();

        this.appClient.events.local.on("route-changed", this.handleRouteChange);
        this.appClient.events.local.on("USER_LOGGED_IN", this.handleLoginLogout);
        this.appClient.events.local.on("USER_LOGGED_OUT", this.handleLoginLogout);

        window.addEventListener('resize', this.debouncedHandleResize);
        document.addEventListener('show.bs.modal', this.modalShowHandler);
        document.addEventListener('hidden.bs.modal', this.modalHideHandler);

        await super.initialize();
    }

    updateLayoutType() {
        if (this.config.layout === 'vertical') {
            this.isVertical = true;
        } else if (this.config.layout === 'horizontal') {
            this.isVertical = false;
        } else {
            if (window.innerWidth < 768) {
                this.isVertical = this.config.mobileBehavior === 'sidebar';
            } else {
                this.isVertical = true;
            }
        }
    }

    adjustMenuRelatedViewportHeight() {
        // No-op: dynamic viewport height is replaced by framework-layout.css
    }

async render() {
        this.updateLayoutType();
        // Build container root if first time
        if (!this.container) {
            this.container = document.createElement("div");
            this.container.id = this.containerId;
        }

        // Base classes
        this.container.className = 'app-menu-container';
        if (this.isVertical) {
            this.container.classList.add("vertical");
            this.container.classList.toggle("app-menu-full-height", this.config.fullHeight);
            this.container.classList.toggle("app-menu-auto-height", !this.config.fullHeight);
            if (this.config.collapsible) {
                this.container.classList.add("collapsible");
                this.container.classList.toggle("icons-only", this.isCollapsed);
            }
        } else {
            this.container.classList.add("horizontal");
        }

        // Full rebuild?
        if (this._needsFullRender) {
            this.container.innerHTML = '';

            // Items container if vertical
            if (this.isVertical) {
                this.itemsContainer = document.createElement("div");
                this.itemsContainer.className = 'menu-items-scroll-area';
                this.container.appendChild(this.itemsContainer);
            } else {
                this.itemsContainer = this.container;
            }

            // --- MODIFICATION START ---
            let menuItemsToRender = this.menuData;

            // Build the hierarchical tree ONLY if submenus are enabled, in vertical mode, AND the menu is expanded.
            if (this.config.showSubmenus && this.isVertical && !this.isCollapsed) {
                const nodeMap = new Map();
                const tree = [];

                this.menuData.forEach(item => {
                    if (!item.route) return;
                    if (!nodeMap.has(item.route)) {
                        nodeMap.set(item.route, { ...item, children: [] });
                    }
                    const segments = item.route.split('/').filter(Boolean);
                    let currentPath = '';
                    for (let i = 0; i < segments.length - 1; i++) {
                        currentPath += `/${segments[i]}`;
                        if (!nodeMap.has(currentPath)) {
                             const title = segments[i].charAt(0).toUpperCase() + segments[i].slice(1).replace(/-/g, ' ');
                             nodeMap.set(currentPath, {
                                 route: currentPath,
                                 title: title,
                                 icon: 'bi-collection-fill',
                                 children: [],
                                 isVirtual: true,
                                 order: nodeMap.get(item.route)?.order ?? 999
                             });
                        }
                    }
                });

                nodeMap.forEach(node => {
                    const lastSlashIndex = node.route.lastIndexOf('/');
                    if (lastSlashIndex > 0) {
                        const parentRoute = node.route.substring(0, lastSlashIndex);
                        const parentNode = nodeMap.get(parentRoute);
                        if (parentNode) {
                            parentNode.children.push(node);
                        } else {
                            tree.push(node);
                        }
                    } else {
                        tree.push(node);
                    }
                });
                
                const sortFn = (a, b) => (a.order ?? 999) - (b.order ?? 999);
                tree.sort(sortFn);
                nodeMap.forEach(node => {
                    if (node.children.length > 1) {
                        node.children.sort(sortFn);
                    }
                });

                menuItemsToRender = tree;
            }
            // --- MODIFICATION END ---


            // Build items & route map
            this.routeMap = {};
            this.routesSorted = [];
            // This will now render either the tree or the flat list based on the logic above.
            await this.renderMenuItems(menuItemsToRender, this.itemsContainer);

            // Toggle button
            if (this.isVertical && this.config.collapsible) {
                this.renderToggleButton();
            }

            // Cache sorted routes for quick highlight
            this.routesSorted.sort((a, b) => b.length - a.length);

            // Translations
            this.appClient.tx.applyTranslations(this.container);

            this._needsFullRender = false;
        }

        // Highlight active route
        this.highlightActiveItem();

        // Tooltips only if collapsed
        if (this.isVertical && this.isCollapsed) {
            this.initTooltips();
        } else {
            this.destroyTooltips();
        }

        this.adjustMenuRelatedViewportHeight();
        return this.container;
    }


    async renderMenuItems(items, parent, level = 0) {
        for (const item of items) {
            // This logic is now greatly simplified as we handle parents and children differently
            const hasChildren = item.children && item.children.length > 0 && this.config.showSubmenus && this.isVertical;

            if (hasChildren) {
                // This is a parent item. Render a static header and its children.

                // First, filter children to ensure we don't render an empty group
                const visibleChildren = [];
                for (const child of item.children) {
                    if (!child.route && !child.action) continue;
                    const hasPerm = child.route ? await this.appClient.sessionTokenHandler.hasPermission("routes", child.route) : true;
                    if (hasPerm) {
                        visibleChildren.push(child);
                    }
                }

                // Only render the header if there are visible children
                if (visibleChildren.length > 0) {
                    const submenuHeader = document.createElement("div");
                    submenuHeader.className = "app-submenu-header";
                    submenuHeader.setAttribute('data-i18n', item.title);
                    submenuHeader.textContent = this.t(item.title);
                    // Add a data attribute to find this header later for highlighting
                    submenuHeader.dataset.parentRoute = item.route;
                    parent.appendChild(submenuHeader);

                    // The submenu is now just a simple container, not a collapsible element
                    const submenu = document.createElement("div");
                    submenu.className = "app-submenu";
                    submenu.dataset.parentRoute = item.route;

                    // Render the visible children inside the container
                    await this.renderMenuItems(visibleChildren, submenu, level + 1);
                    parent.appendChild(submenu);
                }

            } else {
                // This is a standard, clickable menu item.
                const hasPerm = item.route ? await this.appClient.sessionTokenHandler.hasPermission("routes", item.route) : true;
                if (!hasPerm) continue;

                const link = document.createElement("a");
                link.className = "app-menu-item";

                if (item.route) {
                    link.href = this.appClient.layoutEngine.prefix + item.route;
                    link.dataset.route = item.route;
                } else {
                    link.href = "#";
                    link.addEventListener('click', e => {
                        e.preventDefault();
                        this.emit(item.action, item);
                    });
                }

                if (item.icon) {
                    const icon = document.createElement("i");
                    icon.className = "app-menu-icon " + item.icon;
                    link.appendChild(icon);
                }

                const label = document.createElement("span");
                label.className = "app-menu-label";
                label.textContent = this.t(item.title);
                label.setAttribute("data-i18n", item.title);
                link.appendChild(label);

                link.title = this.t(item.title);
                link.dataset.bsToggle = 'tooltip';
                link.dataset.bsPlacement = this.isVertical && this.isCollapsed ? 'right' : (this.isVertical ? 'right' : 'top');
                link.dataset.bsTrigger = 'hover';

                parent.appendChild(link);

                if (item.route) {
                    this.routeMap[item.route] = link;
                    this.routesSorted.push(item.route);
                }
            }
        }
    }


    renderToggleButton() {
        const container = this.container;
        // Remove old if any
        if (this.toggleButton?.parentElement) {
            this.toggleButton.parentElement.remove();
            this.toggleButton = null;
        }

        const wrapper = document.createElement("div");
        wrapper.className = 'app-menu-toggle-button-container';
        const btn = document.createElement("button");
        btn.type = 'button';
        btn.className = 'app-menu-toggle-button';
        btn.addEventListener('click', this.toggleCollapse);
        wrapper.appendChild(btn);
        container.appendChild(wrapper);
        this.toggleButton = btn;
        this.updateToggleButtonUI();
    }

    updateToggleButtonUI() {
        if (!this.toggleButton) return;
        if (this.isCollapsed) {
            this.toggleButton.innerHTML = '<i class="bi bi-chevron-double-right"></i>';
            this.toggleButton.title = this.t('Expand Menu');
            this.toggleButton.setAttribute('data-i18n-title', 'Expand Menu');
        } else {
            this.toggleButton.innerHTML = '<i class="bi bi-chevron-double-left"></i>';
            this.toggleButton.title = this.t('Collapse Menu');
            this.toggleButton.setAttribute('data-i18n-title', 'Collapse Menu');
        }
    }

   async toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        if (this.config.saveState && this.config.collapsible) {
            localStorage.setItem(this.localStorageKey, JSON.stringify(this.isCollapsed));
        }
        
        // --- MODIFICATION START ---
        // A full re-render is now required because the menu structure (tree vs. flat list) changes.
        this._needsFullRender = true;
        await this.render();
        // --- MODIFICATION END ---
        
        this.adjustMenuRelatedViewportHeight();
    }


    initTooltips() {
        this.destroyTooltips();
        const els = this.container.querySelectorAll('[data-bs-toggle="tooltip"]');
        this.tooltipInstances = Array.from(els).map(el =>
            new bootstrap.Tooltip(el, {
                trigger: 'hover',
                boundary: this.container,
                customClass: 'app-menu-tooltip'
            })
        );
    }

    destroyTooltips() {
        this.tooltipInstances.forEach(t => t.dispose());
        this.tooltipInstances = [];
    }

    highlightActiveItem() {
        if (!this.routeMap) return;

        // Remove old active states from links AND headers
        this.container.querySelectorAll('.app-menu-item.active, .app-submenu-header.active-parent')
            .forEach(el => {
                el.classList.remove('active', 'active-parent');
            });

        const current = this.appClient.layoutEngine.currentRoute || '';
        let activeRoute = null;

        for (const route of this.routesSorted) {
            if (current.startsWith(route)) {
                activeRoute = route;
                break;
            }
        }

        if (activeRoute) {
            const activeEl = this.routeMap[activeRoute];
            if (activeEl) {
                activeEl.classList.add('active');
                // Find the parent submenu container, then find its corresponding header
                const submenu = activeEl.closest('.app-submenu');
                if (submenu) {
                    const parentRoute = submenu.dataset.parentRoute;
                    if (parentRoute) {
                        // Find the header using its data attribute and apply the highlight class
                        const headerEl = this.container.querySelector(`.app-submenu-header[data-parent-route="${parentRoute}"]`);
                        if (headerEl) {
                            headerEl.classList.add('active-parent');
                        }
                    }
                }
            }
        }
    }



    async handleResize() {
        const prev = this.isVertical;
        this.updateLayoutType();
        if (prev !== this.isVertical) {
            // full rebuild if layout orientation changed
            if (this.config.collapsible && !this.config.saveState) {
                this.isCollapsed = this.config.defaultCollapsed;
            }
            this._needsFullRender = true;
            await this.render();
        }
        this.adjustMenuRelatedViewportHeight();
    }

    handleRouteChange() {
        this.highlightActiveItem();
    }

    async handleLoginLogout() {
        if (this.config.dataSource === "layoutEngine") {
            this.menuData = this.config.showSubmenus
                ? this.appClient.layoutEngine.getMenuStructure(
                    this.config.startingRoute,
                    this.config.includeHidden
                )
                : this.appClient.layoutEngine.getFlatMenuStructure(
                    this.config.startingRoute,
                    this.config.includeHidden
                );
        }
        this._needsFullRender = true;
        if (this.container) {
            await this.render();
        }
    }

    handleModalVisibilityChange(shown) {
        if (!this.isVertical && this.container) {
            this.container.style.display = shown ? 'none' : 'flex';
        }
    }

    destroy() {
        this.appClient.events.local.off("route-changed", this.handleRouteChange);
        this.appClient.events.local.off("USER_LOGGED_IN", this.handleLoginLogout);
        this.appClient.events.local.off("USER_LOGGED_OUT", this.handleLoginLogout);

        window.removeEventListener('resize', this.debouncedHandleResize);
        document.removeEventListener('show.bs.modal', this.modalShowHandler);
        document.removeEventListener('hidden.bs.modal', this.modalHideHandler);

        this.destroyTooltips();
        super.destroy();
    }
}